<template>
  <div
    v-if="canDrafts || canNewsletters"
    class="newsletters-list"
  >
    <page-header header="Newsletters">
      <router-link
        v-if="$login.can('newsletter-drafts.create')"
        class="btn btn-primary "
        :to="{name: 'newsletters.create'}"
      >
        New Newsletter
      </router-link>
    </page-header>

    <div
      v-if="canDrafts && (!draftsReady || drafts.length)"
      class="my-4"
    >
      <h2 class="mb-2">
        Draft Newsletters
      </h2>
      <newsletters-table
        v-if="draftsReady"
        :value="(v) => { return v.latestRevision || {} }"
        :newsletters="drafts"
        :is-drafts-list="true"
        @delete="showDeleteDraftModal($event)"
      />

      <div
        v-else
        class="loading-wrapper w-100 py-3 chart-grey-background"
      >
        <loading-icon
          :loading="!draftsReady"
          loading-class="text-center"
        />
      </div>
    </div>

    <div
      v-if="canNewsletters"
      class="mt-2 mb-4"
    >
      <div class="d-flex my-2">
        <h2 class="mb-0 align-self-center">
          Published Newsletters
        </h2>
        <div
          v-if="newslettersReady"
          class="ml-auto"
        >
          <search
            v-model="params.search"
            @input="updateSearchParam"
          />
        </div>
      </div>
      <newsletters-table
        v-if="newslettersReady"
        :newsletters="newsletters"
        :pagination="pagination"
        :loading="loading"
        :value="(v) => { return v.publishedRevision || {} }"
        @change-page="changePage"
        @show-alerts="showAlerts"
        @delete="showDeleteModal($event)"
      />

      <div
        v-else
        class="loading-wrapper w-100 py-3 chart-grey-background"
      >
        <loading-icon
          :loading="!newslettersReady"
          loading-class="text-center"
        />
      </div>
    </div>

    <modal
      :classes="['v--modal', 'vue-dialog']"
      :pivot-y="0.3"
      :adaptive="true"
      :scrollable="true"
      height="auto"
      name="create-newsletter"
    >
      <create-modal
        modal="create-newsletter"
        @save-success="createdNewsletter"
      />
    </modal>

    <confirm
      v-if="selectedDraft"
      :name="selectedDraft.latestRevision.subject || 'Untitled'"
      @confirmed="removeDraft"
    />

    <confirm
      v-if="selectedNewsletter"
      :name="selectedNewsletter.subject"
      @confirmed="removeNewsletter"
    />

    <alerts-modal
      v-if="alertNewsletterId"
      :newsletter-id="alertNewsletterId"
    />
  </div>
</template>

<script>
import gateway from '@/gateway'
import CreateModal from '@/views/newsletters/CreateModal'
import NewslettersTable from '@/views/newsletters/Table'
import AlertsModal from '@/views/articles/AlertsModal'
import Search from '@/components/Search'
import Confirm from '@/components/Confirm'
import {successNotification} from '@/notifications'

export default {
  name: 'NewsletterList',

  components: {
    CreateModal,
    Confirm,
    NewslettersTable,
    AlertsModal,
    Search,
  },

  data () {
    const params = {
      page: 1,
      // filters: [],
      // search: null,
      // channel_id: null,
      ...this.$route.query,
    }

    return {
      newsletters: null,
      drafts: null,
      pagination: null,
      loading: false,
      params,
      showDeleted: false,
      alertNewsletterId: null,
      newlyCreatedArticle: null,
      selectedDraft: null,
      selectedNewsletter: null,
    }
  },

  watch: {
    showDeleted () {
      const index = this.params.filters.indexOf('with_deleted')
      if (index !== -1) {
        this.params.filters.splice(index, 1)
      } else {
        this.params.filters.push('with_deleted')
      }
    },
  },

  computed: {
    draftsReady () {
      return this.drafts !== null
    },

    newslettersReady () {
      return this.newsletters !== null
    },

    canDrafts () {
      return this.$login.can('newsletter-drafts.view') || this.$login.can('newsletter-drafts.view.mine')
    },

    canNewsletters () {
      return this.$login.can('newsletters.view') || this.$login.can('newsletters.view.mine')
    },
  },

  metaInfo () {
    return {
      title: 'Newsletters',
    }
  },

  created () {
    this.getNewsletters()
  },

  methods: {
    async getNewsletters () {
      this.loading = true

      if (this.canDrafts) {
        const response = await gateway.get('/newsletters/drafts', {finishProgressBar: false})
        this.drafts = response.data.data
      }

      if (this.canNewsletters) {
        const {data: newsletters} = await gateway.get('/newsletters', {params: this.params})
        this.pagination = newsletters.meta
        this.newsletters = newsletters.data
      }

      this.loading = false
    },

    showDeleteDraftModal (draft) {
      this.selectedDraft = draft
      this.$nextTick(() => this.$modal.show('confirm-delete'))
    },

    showDeleteModal (article) {
      this.selectedNewsletter = article
      this.$nextTick(() => this.$modal.show('confirm-delete'))
    },

    changePage (page = 1) {
      this.params.page = page
      const {params} = this
      history.pushState({}, null, this.$router.resolve({query: {...params, page}}).href)
      this.getNewsletters()
    },

    updateSearchParam (search = '') {
      if (this.params.search != search) {
        this.params.search = search
        this.params.page = 1
        const {params} = this
        history.pushState({}, null, this.$router.resolve({query: {...params}}).href)
        this.getNewsletters()
      }
    },

    removeNewsletter () {
      gateway.delete(`/newsletters/${this.selectedNewsletter.id}`).then(() => {
        this.removedNewsletter()
      })
    },

    removeDraft () {
      gateway.delete(`/newsletters/drafts/${this.selectedDraft.id}`).then(() => {
        this.removedNewsletter(true)
      })
    },

    removedNewsletter (draft = false) {
      const data = draft ? this.selectedDraft.latestRevision : this.selectedNewsletter

      this.$notify(successNotification(`${data.subject || 'The newsletter'} has been deleted.`))

      if (!draft) {
        this.newsletters.splice(this.newsletters.indexOf(this.selectedNewsletter), 1)
        this.selectedNewsletter = null
      } else {
        this.drafts.splice(this.drafts.indexOf(this.selectedDraft), 1)
        this.selectedDraft = null
      }
    },

    showAlerts (newsletterId) {
      this.alertNewsletterId = newsletterId
      this.$nextTick(() => this.$modal.show('alerts'))
    },

    selectedChannel (selected) {
      this.params.channel_id = selected ? selected.object.id : null
    },
    restore (article) {
      this.$http.patch('/newsletters/' + article.id + '/restore')
          .then((response) => {
            this.newsletters[this.companies.indexOf(article)] = response.data
            this.$notify(successNotification(`Restored ${article.subject}.`))
          })
    },

    createdNewsletter (response) {
      this.$modal.hide('create-newsletter')
      this.$nextTick(() => {
        this.$router.push({
          name: 'newsletters.draft',
          params: {
            draftId: response.data.data.id,
          },
        })
      })
    },
  },
}
</script>
